import React, { useState } from 'react';
import { Project, ProjectStatus, User } from '../types';
import { STATUS_CONFIG } from '../data/mockData.ts';
import { formatFileSize } from '../utils/helper.ts';
import { PermissionGate } from './PermissionGate.tsx';
import { Permissions } from '../constants/permissions.ts';
import { AuthUser, useAuth } from '../context/AuthContext.tsx';
import PergolaViewer from './visualization/PergolaViewer.tsx';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../services/api.ts';
import { useProjects } from '../context/ProjectContext.tsx';
import { Trash, ChevronDown, ChevronUp, Edit, Check, X, MoreHorizontal, ArrowUpRight, PaperclipIcon, Eye } from 'lucide-react';

interface StatusProgressProps {
  currentStatus: ProjectStatus;
}

interface ProjectListProps {
  projects: Project[];
  currentUser: AuthUser;
  onUpdateProjectStatus: (projectId: string, newStatus: ProjectStatus) => Promise<void>;
  onProjectUpdated?: (projectId: string) => void;
}

const StatusProgress: React.FC<StatusProgressProps> = ({ currentStatus }) => {
  const orderedStatuses: ProjectStatus[] = ['Draft', 'Submitted', 'Approved', 'Archived'];
  const currentIndex = orderedStatuses.indexOf(currentStatus);
  const { t } = useTranslation();

  return (
    <div className="hidden sm:flex items-center space-x-2 w-full mt-2">
      {orderedStatuses.map((status, index) => {
        const isCompleted = index <= currentIndex;
        const isLast = index === orderedStatuses.length - 1;

        return (
          <React.Fragment key={status}>
            <div className="flex items-center">
              <div
                className={`w-6 h-6 rounded-full flex items-center justify-center
                  ${isCompleted ? 'bg-green-500' : 'bg-gray-200'}`}
              >
                {isCompleted && (
                  <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </div>
              <span className="ml-2 text-xs sm:text-sm font-medium">{t(`projectList.status.${status.toLowerCase()}`)}</span>
            </div>
            {!isLast && (
              <div className={`flex-1 h-1 ${isCompleted ? 'bg-green-500' : 'bg-gray-200'}`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

// Mobile-friendly status indicator
const MobileStatusIndicator: React.FC<StatusProgressProps> = ({ currentStatus }) => {
  const { t } = useTranslation();
  
  return (
    <div className="sm:hidden mt-2">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-gray-700">Status:</span>
        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${STATUS_CONFIG[currentStatus]?.color}`}>
          {t(`projectList.status.${currentStatus.toLowerCase()}`)}
        </span>
      </div>
      <div className="mt-2 bg-gray-200 h-2 rounded-full overflow-hidden">
        <div 
          className="bg-green-500 h-full rounded-full" 
          style={{ 
            width: currentStatus === 'Draft' ? '25%' : 
                  currentStatus === 'Submitted' ? '50%' : 
                  currentStatus === 'Approved' ? '75%' : '100%' 
          }}
        />
      </div>
    </div>
  );
};

interface ProjectActionsProps {
  project: Project;
  currentUser: AuthUser;
  onUpdateProjectStatus: (projectId: string, newStatus: ProjectStatus) => Promise<void>;
  onDeleteProject: (projectId: string) => void;
}

const ProjectActions: React.FC<ProjectActionsProps> = ({ 
  project, 
  currentUser,
  onUpdateProjectStatus,
  onDeleteProject
}) => {
  const { hasPermission } = useAuth();
  const { t } = useTranslation();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [showMoreActions, setShowMoreActions] = useState(false);
  const actionsRef = React.useRef<HTMLDivElement>(null);

  // Close the actions menu when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (actionsRef.current && !actionsRef.current.contains(event.target as Node)) {
        setShowMoreActions(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [actionsRef]);

  // Only show actions if user has permission and project is their own (for field users)
  const canManageProject = hasPermission(Permissions.VIEW_ALL_PROJECTS) || 
    project.createdBy === Number(currentUser.id);

  const handleChangeOwner = async (projectId: number, newOwnerId: number) => {
    await api.put(`projects/${projectId}/change-owner`, { newOwnerId });
  };

  const handleChangeOwnerClick = async () => {
    const newOwnerId = prompt(t('projectList.actions.enterNewOwnerId'));
    if (newOwnerId) {
      try {
        await handleChangeOwner(Number(project.id), parseInt(newOwnerId));
        alert(t('projectList.actions.ownerChanged'));
      } catch (error) {
        console.error(t('errors.project.changeOwnerError'), error);
        alert(t('errors.project.changeOwnerError'));
      }
    }
  };

  if (!canManageProject) {
    return null;
  }

  // Primary actions shown directly
  const renderPrimaryActions = () => (
    <div className="flex flex-wrap gap-2">
      <PermissionGate permission={Permissions.APPROVE_PROJECT}>
        {project.status === 'Submitted' && (
          <>
            <button
              onClick={() => onUpdateProjectStatus(project.id, 'Approved')}
              className="px-2 py-1 text-xs sm:px-3 sm:py-1 sm:text-sm bg-green-500 text-white rounded-md hover:bg-green-600 flex items-center"
            >
              <Check size={14} className="mr-1" />
              {t('projectList.actions.approve')}
            </button>
            <button
              onClick={() => onUpdateProjectStatus(project.id, 'Draft')}
              className="px-2 py-1 text-xs sm:px-3 sm:py-1 sm:text-sm bg-red-500 text-white rounded-md hover:bg-red-600 flex items-center"
            >
              <X size={14} className="mr-1" />
              {t('projectList.actions.decline')}
            </button>
          </>
        )}
      </PermissionGate>
      
      <PermissionGate permission={Permissions.EDIT_PROJECT}>
        <button
          onClick={() => setIsEditModalOpen(true)}
          className="px-2 py-1 text-xs sm:px-3 sm:py-1 sm:text-sm bg-blue-500 text-white rounded-md hover:bg-blue-600 flex items-center"
        >
          <Edit size={14} className="mr-1" />
          {t('projectList.actions.edit')}
        </button>
      </PermissionGate>
    </div>
  );

  // Secondary actions in dropdown
  const renderMoreActions = () => (
    <div className="relative" ref={actionsRef}>
      <button
        onClick={() => setShowMoreActions(!showMoreActions)}
        className="px-2 py-1 text-xs sm:px-3 sm:py-1 sm:text-sm bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 flex items-center"
      >
        <MoreHorizontal size={14} className="mr-1" />
        <span className="hidden sm:inline">More</span>
      </button>
      
      {showMoreActions && (
        <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-10 ring-1 ring-black ring-opacity-5">
          <PermissionGate permission={Permissions.DELETE_PROJECT}>
            <button
              onClick={() => {
                onDeleteProject(project.id);
                setShowMoreActions(false);
              }}
              className="w-full text-left block px-4 py-2 text-sm text-red-600 hover:bg-gray-100 flex items-center"
            >
              <Trash size={14} className="mr-2" />
              {t('projectList.actions.delete')}
            </button>
          </PermissionGate>

          <PermissionGate permission={Permissions.MANAGE_USERS}>
            <button
              onClick={() => {
                handleChangeOwnerClick();
                setShowMoreActions(false);
              }}
              className="w-full text-left block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
            >
              <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              {t('projectList.actions.changeOwner')}
            </button>
          </PermissionGate>
        </div>
      )}
    </div>
  );

  return (
    <>
      <div className="mt-4 flex flex-wrap gap-2 items-center justify-between">
        {renderPrimaryActions()}
        {renderMoreActions()}
      </div>

      <EditProjectModal
        project={project}
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
      />
    </>
  );
};

const EditProjectModal: React.FC<{
  project: Project;
  isOpen: boolean;
  onClose: () => void;
}> = ({ project, isOpen, onClose }) => {
  const [editedProject, setEditedProject] = useState<Project>({ ...project });
  const { t } = useTranslation();
  const { fetchProjects } = useProjects();

  const handleSave = async () => {
    try {
      await api.put(`projects/${project.id}`, {
        id: project.id,
        name: editedProject.name,
        client: editedProject.client,
        location: editedProject.location,
        description: editedProject.description,
        status: project.status // Preserve existing status
      });
      
      // Refresh projects list
      fetchProjects();
      onClose();
    } catch (error) {
      console.error('Error saving project:', error);
      // Optionally show an error message to the user
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg p-4 sm:p-6 w-full max-w-md max-h-[90vh] overflow-auto">
        <h2 className="text-xl font-bold mb-4">
          {t('projectList.actions.edit')}
        </h2>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">
              {t('projectForm.fields.name.label')}
            </label>
            <input
              type="text"
              value={editedProject.name}
              onChange={(e) => setEditedProject(prev => ({ ...prev, name: e.target.value }))}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm py-2 px-3 border"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">
              {t('projectForm.fields.client.label')}
            </label>
            <input
              type="text"
              value={editedProject.client}
              onChange={(e) => setEditedProject(prev => ({ ...prev, client: e.target.value }))}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm py-2 px-3 border"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">
              {t('projectForm.fields.location.label')}
            </label>
            <input
              type="text"
              value={editedProject.location}
              onChange={(e) => setEditedProject(prev => ({ ...prev, location: e.target.value }))}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm py-2 px-3 border"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">
              {t('projectForm.fields.description.label')}
            </label>
            <textarea
              value={editedProject.description}
              onChange={(e) => setEditedProject(prev => ({ ...prev, description: e.target.value }))}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm py-2 px-3 border"
              rows={3}
            />
          </div>
          <div className="flex justify-end space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700"
            >
              {t('projectForm.buttons.cancel')}
            </button>
            <button
              onClick={handleSave}
              className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
            >
              {t('projectForm.buttons.save')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Attachment list component with collapsible behavior
const AttachmentList: React.FC<{
  attachments: any[];
  projectId: string;
  onDeleteAttachment: (projectId: string, attachmentId: number) => void;
}> = ({ attachments, projectId, onDeleteAttachment }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const { t } = useTranslation();
  const displayCount = isExpanded ? attachments.length : Math.min(2, attachments.length);

  if (attachments.length === 0) {
    return <p className="text-sm text-gray-500">{t('projectList.noAttachments')}</p>;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-sm font-medium text-gray-700 flex items-center">
          <PaperclipIcon size={16} className="mr-1" />
          {t('projectList.attachments.title')} ({attachments.length})
        </h4>
        {attachments.length > 2 && (
          <button 
            onClick={() => setIsExpanded(!isExpanded)}
            className="text-xs text-indigo-600 hover:text-indigo-800 flex items-center"
          >
            {isExpanded ? (
              <>
                <ChevronUp size={14} className="mr-1" />
                {t('projectList.visualization.collapse')}
              </>
            ) : (
              <>
                <ChevronDown size={14} className="mr-1" />
                {t('projectList.visualization.expand')}
              </>
            )}
          </button>
        )}
      </div>
      <div className="space-y-2">
        {attachments.slice(0, displayCount).map(file => (
          <div key={file.id} className="flex items-center justify-between bg-gray-50 p-2 rounded text-xs sm:text-sm">
            <span className="truncate max-w-[120px] sm:max-w-xs">{file.name}</span>
            <div className="flex items-center space-x-2">
              <span className="text-xs text-gray-500 hidden sm:inline">
                {file.type} - {formatFileSize(file.size)}
              </span>
              <button
                onClick={() => onDeleteAttachment(projectId, file.id)}
                className="text-red-500 hover:text-red-700 p-1"
                aria-label="Delete attachment"
              >
                <Trash size={14} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const ProjectCard: React.FC<{
  project: Project;
  currentUser: AuthUser;
  onUpdateProjectStatus: (projectId: string, newStatus: ProjectStatus) => Promise<void>;
  onDeleteAttachment: (projectId: string, attachmentId: number) => void;
  onDeleteProject: (projectId: string) => void;
}> = ({ project, currentUser, onUpdateProjectStatus, onDeleteAttachment, onDeleteProject }) => {
  const { t } = useTranslation();
  const [isVisualizationExpanded, setVisualizationExpanded] = useState(false);
  
  return (
    <div className="bg-white rounded-lg shadow p-4 sm:p-6">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-2 mb-4">
        <div className="flex-1">
          <h3 className="text-lg sm:text-xl font-semibold">{project.name}</h3>
          <p className="text-xs sm:text-sm text-gray-500">{t('projectList.projectInfo.id')}: {project.id}</p>
          <p className="text-xs sm:text-sm text-gray-500">{t('projectList.projectInfo.createdBy')}: {project.createdByUser}</p>
        </div>
        <span className={`px-2 py-1 text-xs rounded-full inline-flex items-center ${STATUS_CONFIG[project.status]?.color} self-start`}>
          {STATUS_CONFIG[project.status]?.label}
        </span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
        <div>
          <p className="text-xs sm:text-sm font-medium text-gray-500">{t('projectList.projectInfo.client')}</p>
          <p className="text-sm sm:text-base truncate">{project.client}</p>
        </div>
        <div>
          <p className="text-xs sm:text-sm font-medium text-gray-500">{t('projectList.projectInfo.location')}</p>
          <p className="text-sm sm:text-base truncate">{project.location}</p>
        </div>
        <div>
          <p className="text-xs sm:text-sm font-medium text-gray-500">{t('projectList.projectInfo.created')}</p>
          <p className="text-sm sm:text-base">{new Date(project.createdAt).toLocaleDateString()}</p>
        </div>
        <div>
          <p className="text-xs sm:text-sm font-medium text-gray-500">{t('projectList.projectInfo.lastModified')}</p>
          <p className="text-sm sm:text-base">{new Date(project.lastModified).toLocaleDateString()}</p>
        </div>
      </div>

      <div className="mb-4">
        <AttachmentList 
          attachments={project.attachments || []} 
          projectId={project.id}
          onDeleteAttachment={onDeleteAttachment}
        />
      </div>

      <div className="border-t pt-4">
        <StatusProgress currentStatus={project.status} />
        <MobileStatusIndicator currentStatus={project.status} />
        <ProjectActions 
          project={project} 
          currentUser={currentUser} 
          onUpdateProjectStatus={onUpdateProjectStatus} 
          onDeleteProject={onDeleteProject}
        />
      </div>

      <div className="border-t pt-4 mt-4">
        <div className="flex flex-wrap justify-between items-center mb-4">
          <h4 className="text-base font-medium flex items-center">
            <Eye size={16} className="mr-1" />
            {t('projectList.visualization.title')}
          </h4>
          <div className="flex gap-2 mt-2 sm:mt-0">
            <button
              onClick={() => setVisualizationExpanded(!isVisualizationExpanded)}
              className="px-2 py-1 text-xs sm:px-3 sm:py-1 sm:text-sm bg-gray-100 hover:bg-gray-200 rounded-md flex items-center"
            >
              {isVisualizationExpanded ? (
                <>
                  <ChevronUp size={14} className="mr-1" />
                  {t('projectList.visualization.collapse')}
                </>
              ) : (
                <>
                  <ChevronDown size={14} className="mr-1" />
                  {t('projectList.visualization.expand')}
                </>
              )}
            </button>
            <Link
              to={`/projects/${project.id}/visualization`}
              className="px-2 py-1 text-xs sm:px-3 sm:py-1 sm:text-sm bg-blue-500 text-white hover:bg-blue-600 rounded-md flex items-center"
            >
              <ArrowUpRight size={14} className="mr-1" />
              {t('projectList.visualization.fullScreen')}
            </Link>
          </div>
        </div>
        
        {isVisualizationExpanded && (
          <div className="h-[300px] sm:h-[400px] md:h-[500px] relative border rounded-lg">
            <PergolaViewer />
          </div>
        )}
      </div>
    </div>
  );
};

const ProjectList: React.FC<ProjectListProps> = ({ projects, currentUser, onUpdateProjectStatus }) => {
  const { t } = useTranslation();
  const { fetchProjects } = useProjects();
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<{
    isOpen: boolean;
    projectId?: string;
    attachmentId?: number;
  }>({ isOpen: false });
  
  const onDeleteAttachment = (projectId: string, attachmentId: number) => {
    setDeleteConfirm({ isOpen: true, projectId, attachmentId });
  };
  
  const handleDeleteAttachment = async (projectId: string, attachmentId: number) => {
    if (isDeleting) return; // Prevent multiple clicks
    
    if (window.confirm('Are you sure you want to delete this attachment?')) {
      try {
        setIsDeleting(true);
        await api.delete(`projects/${projectId}/attachments/${attachmentId}`);
        alert('Attachment deleted successfully');
        // Reload the page to reflect changes
        window.location.reload();
      } catch (error) {
        console.error('Error deleting attachment:', error);
        alert('Failed to delete attachment. Please try again.');
      } finally {
        setIsDeleting(false);
      }
    }
  };

  const handleDeleteProject = (projectId: string) => {
    setDeleteConfirm({ 
      isOpen: true, 
      projectId: projectId 
    });
  };

  const confirmDeleteProject = async () => {
    if (!deleteConfirm.projectId) return;
    
    try {
      setIsDeleting(true);
      await api.delete(`projects/${deleteConfirm.projectId}`);
      // Refresh the projects list
      fetchProjects();
      setDeleteConfirm({ isOpen: false });
    } catch (error) {
      console.error('Error deleting project:', error);
      alert('Failed to delete project. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (deleteConfirm.attachmentId) {
      // Handle attachment deletion
      try {
        await api.delete(`projects/${deleteConfirm.projectId}/attachments/${deleteConfirm.attachmentId}`);
        // Refresh projects after deletion
        fetchProjects();
      } catch (error) {
        console.error('Error deleting attachment:', error);
      } finally {
        setDeleteConfirm({ isOpen: false });
      }
    } else if (deleteConfirm.projectId) {
      // Handle project deletion
      await confirmDeleteProject();
    }
  };

  return (
    <div className="space-y-4">
      {projects.map((project) => (
        <ProjectCard
          key={project.id}
          project={project}
          currentUser={currentUser}
          onUpdateProjectStatus={onUpdateProjectStatus}
          onDeleteAttachment={onDeleteAttachment}
          onDeleteProject={handleDeleteProject}
        />
      ))}
      
      {/* Delete Confirmation Dialog */}
      {deleteConfirm.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white p-4 sm:p-6 rounded-lg shadow-xl max-w-md w-full">
            <h3 className="text-lg font-medium mb-4">
              {deleteConfirm.attachmentId 
                ? t('projectList.confirmDialog.deleteAttachment') 
                : t('projectList.confirmDialog.deleteProject')}
            </h3>
            <p className="text-gray-600 mb-6 text-sm sm:text-base">
              {deleteConfirm.attachmentId 
                ? t('projectList.confirmDialog.deleteAttachmentMessage')
                : t('projectList.confirmDialog.deleteProjectMessage')}
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setDeleteConfirm({ isOpen: false })}
                disabled={isDeleting}
                className="px-3 py-1.5 sm:px-4 sm:py-2 text-sm text-gray-600 hover:text-gray-800"
              >
                {t('projectList.confirmDialog.cancel')}
              </button>
              <button
                onClick={handleDeleteConfirm}
                disabled={isDeleting}
                className="px-3 py-1.5 sm:px-4 sm:py-2 text-sm bg-red-600 text-white rounded hover:bg-red-700 flex items-center gap-2"
              >
                {isDeleting ? t('projectList.confirmDialog.deleting') : t('projectList.confirmDialog.confirm')}
                {isDeleting && (
                  <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectList;